/**
 * Read-Only Baseline-Snapshot-Builder für PEPlan (Planungsrechner).
 * Zieht alle Ausgangswerte tiefenkopiert aus der aktiven Mandantenakte.
 * STRIKT: Keine Objektreferenzen auf den Live-State, kein Write-Back!
 */
import type { ClientDataState, SettingsState } from '../store/types';
import { calculateAllMetrics } from '../store/selectors/calculations';
import { isHj1ClientLocked } from './hjLock';
import { isSoloModeActive } from './soloMode';
import { calculateConvergedUnternehmerStundenlohn } from './unternehmerlohnConvergence';
import type {
  PlanningBaselineData,
  PlanningBaselineEmployee,
  PlanningDeviceItem,
  PlanningGlobalSettings,
  PlanningMaterialItem,
} from './planningEngine';

export type AllMetricsResult = ReturnType<typeof calculateAllMetrics>;

const parseVal = (v: unknown, fallback = 0): number => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : fallback;
};

/**
 * Berechnet den Ø historischen Monatsumsatz (Brutto) eines Mitarbeiters aus `mitarbeiterLeistung`.
 * Falls keine Monatsumsätze vorliegen, wird 0 zurückgegeben (Engine nutzt dann Soll-Umsatz).
 */
function resolveEmployeeHistoricalMonthlyGross(
  employeeId: string,
  mitarbeiterLeistungMap: Record<string, any> | undefined,
  isHJ2Freigegeben: boolean
): number {
  if (!employeeId || !mitarbeiterLeistungMap) return 0;
  const raw = mitarbeiterLeistungMap[employeeId];
  if (!raw) return 0;

  const months = [
    ...(Array.isArray(raw.halbjahr1) ? raw.halbjahr1 : []),
    ...(isHJ2Freigegeben && Array.isArray(raw.halbjahr2) ? raw.halbjahr2 : []),
  ];
  const active = months.filter(
    (m: any) => parseVal(m?.bedienUmsatz) + parseVal(m?.vkUmsatz) > 0
  );
  if (active.length === 0) return 0;

  const total = active.reduce(
    (sum: number, m: any) => sum + parseVal(m?.bedienUmsatz) + parseVal(m?.vkUmsatz),
    0
  );
  return total / active.length;
}

/**
 * Tiefenkopierter Builder für `PlanningBaselineData` und `PlanningGlobalSettings`.
 */
export function buildPlanningBaselineFromLive(
  clientData: ClientDataState | any,
  calculations: AllMetricsResult | any,
  settings: SettingsState | any,
  meta?: { mandantUid?: string | null; year?: string | null }
): {
  baseline: PlanningBaselineData;
  globalSettings: PlanningGlobalSettings;
} {
  const cd = clientData || {};
  const st = settings || {};

  // Gleiche Unternehmer-Stundenlohn-Konvergenz wie LohnfaktorPage / SystemBenchmark:
  // Ohne Besuch der LohnfaktorPage stünde sonst oft noch 13 € Default → Baseline-Lohnfaktor falsch (3,50 statt z. B. 3,68).
  let effectiveCd = cd;
  let calc = calculations || calculateAllMetrics(cd, st);
  const lfData = cd?.lohnfaktorData || {};
  const isManualStundenlohnActive = lfData?.isManualStundenlohnActive === true;
  const chefHours = parseVal(calc?.monatsstundenChef);
  if (!isManualStundenlohnActive && cd?.unternehmensform !== 'GmbH' && chefHours > 0) {
    const bedarf = parseVal(calc?.echterBedarfGesamt ?? calc?.gesamtlohn);
    const prodUL = parseVal(calc?.prodUnternehmerlohn);
    const unprodUL = parseVal(calc?.unprodUnternehmerlohn);
    const sollumsatzOhneUnternehmerlohn = parseVal(calc?.sollumsatz) - prodUL - unprodUL;
    const ohneAzubis =
      parseVal(calc?.produktiveLohnkostenMitarbeiterOhneAzubis) ||
      Math.max(0, parseVal(calc?.produktiveKostenTabelle) - prodUL);
    const lohnfaktorNennerMitarbeiterOhneAzubis = Math.max(
      0,
      parseVal(calc?.produktiveKostenTabelle) - prodUL
    );
    const isSolo =
      isSoloModeActive(cd) || Number(calc?.personalData?.length || 0) === 0;
    const rawSf = lfData?.sicherheitsfaktor;
    const sfPct =
      rawSf === null || rawSf === undefined || rawSf === ''
        ? 25
        : parseVal(rawSf, 25);

    const ideal = calculateConvergedUnternehmerStundenlohn({
      isSolo,
      echterBedarfGesamt: bedarf,
      chefMonatsstunden: chefHours,
      sollumsatzOhneUnternehmerlohn,
      produktiveLohnkostenMitarbeiterOhneAzubis: ohneAzubis,
      lohnfaktorNennerMitarbeiterOhneAzubis,
      kalkulierterUnternehmerUmsatzBrutto: parseVal(calc?.kalkulierterUnternehmerUmsatzBrutto),
      mwstSatz: parseVal(calc?.mwstSatz, 19),
      sicherheitsfaktorPercent: sfPct,
      urlaubsausgleichPercent: parseVal(calc?.lohnfortzahlungPercent, 20) || 20,
      initialGuess:
        parseVal(calc?.realisierterStundenlohnChef) ||
        parseVal(lfData?.unternehmerStundenlohn_editable) ||
        0,
    });

    const currentSoll = parseVal(lfData?.unternehmerStundenlohn_editable);
    if (ideal > 0 && Math.abs(currentSoll - ideal) >= 0.005) {
      effectiveCd = {
        ...cd,
        lohnfaktorData: {
          ...lfData,
          unternehmerStundenlohn_editable: ideal,
        },
      };
      calc = calculateAllMetrics(effectiveCd, st);
    }
  }

  const activeLandRaw = String(
    calc.activeLand || cd.Land || cd.land || st.selectedCountryId || 'DE'
  ).toUpperCase();
  const activeLand: 'DE' | 'AT' | 'CH' | 'IT' =
    activeLandRaw === 'AT' || activeLandRaw === 'CH' || activeLandRaw === 'IT'
      ? activeLandRaw
      : 'DE';

  const currencyCode = String(
    calc.currencyCode || st?.country?.currency || (activeLand === 'CH' ? 'CHF' : 'EUR')
  );

  const mwstSatzPercent =
    calc.mwstSatz !== undefined && calc.mwstSatz !== null
      ? parseVal(calc.mwstSatz, 19)
      : parseVal(st?.country?.MwSt_Satz, 19);

  const lohnnebenkostenPercent =
    parseVal(calc.avgSozialabgabenPercent) > 0
      ? parseVal(calc.avgSozialabgabenPercent)
      : parseVal(st?.country?.Lohnnebenkosten, 22.5);

  const urlaubsausgleichPercent = parseVal(
    calc.lohnfortzahlungPercent ?? st?.global?.Urlaubsausgleich,
    20
  );

  const rawSicherheitsfaktor = cd?.lohnfaktorData?.sicherheitsfaktor;
  const sicherheitsfaktorPercent =
    rawSicherheitsfaktor === null ||
    rawSicherheitsfaktor === undefined ||
    rawSicherheitsfaktor === ''
      ? 25
      : parseVal(rawSicherheitsfaktor, 25);

  const verlustPercent = parseVal(
    calc.verlustPercent ?? cd?.verlust1 ?? st?.global?.Verlust_1,
    15
  );

  const ruecklagenSatzPercent = parseVal(
    cd?.liquiditaet ?? st?.global?.Liquiditaet,
    10
  );

  const wochensatz = parseVal(calc.wochensatz ?? st?.global?.Wochensatz, 4.33) || 4.33;
  const auslastungsfaktorPercent = parseVal(cd?.auslastungsfaktor, 75) || 75;
  const warenwirtschaftAufschlagVk =
    parseVal(st?.global?.Warenwirtschaftsaufschlag_VK, 1.3) || 1.3;

  const globalSettings: PlanningGlobalSettings = {
    mwstSatzPercent,
    lohnnebenkostenPercent,
    urlaubsausgleichPercent,
    sicherheitsfaktorPercent,
    verlustPercent,
    ruecklagenSatzPercent,
    wochensatz,
    auslastungsfaktorPercent,
    warenwirtschaftAufschlagVk,
    standardMaterialGrammProStunde: 50,
    currencyCode,
    activeLand,
  };

  // --- Deep-Clone Mitarbeiter (Produktiv, Unproduktiv, Azubi) ---
  const lohnfaktorAktiv = Math.max(3.5, parseVal(calc.lohnfaktorAktiv, 3.5));
  const isHJ2Freigegeben = Boolean(cd?.isHJ2Freigegeben);
  const hj1Locked = isHj1ClientLocked({
    isHJ1GesperrtGlobal: Boolean(cd?.isHJ1GesperrtGlobal),
    isHJ2Freigegeben,
  });

  const cloneEmployeeList = (
    list: any[],
    scope: 'produktiv' | 'unproduktiv' | 'azubi'
  ): PlanningBaselineEmployee[] => {
    if (!Array.isArray(list)) return [];
    return list.map((ma: any, idx: number): PlanningBaselineEmployee => {
      const id = String(ma?.id || `${scope}_${idx}`);
      const grundgehaltBrutto = parseVal(ma?.grundgehalt ?? ma?.grundlohn);
      const lohnzusatz = parseVal(ma?.lohnzusatz ?? ma?.lohnkostenzusatz);
      const bonus = parseVal(ma?.bonus);
      const wochenstunden = parseVal(ma?.wochenstunden);
      const monatsstunden =
        parseVal(ma?.monatsstunden) > 0
          ? parseVal(ma?.monatsstunden)
          : wochenstunden * wochensatz;
      const arbeitstageProWoche = parseVal(ma?.arbeitstage, 5) || 5;
      const lnkSatz = parseVal(ma?.lohnnebenkostenSatz, lohnnebenkostenPercent);
      const agVollkostenMonat = parseVal(ma?.totalCost ?? ma?.totalPersonnelCost);
      const stundenlohnGesamt =
        parseVal(ma?.stundenlohnGesamt) > 0
          ? parseVal(ma?.stundenlohnGesamt)
          : monatsstunden > 0
            ? (grundgehaltBrutto + lohnzusatz + bonus) / monatsstunden
            : 0;
      const sollUmsatzMonatBrutto = (grundgehaltBrutto + bonus) * lohnfaktorAktiv;
      const histBrutto = resolveEmployeeHistoricalMonthlyGross(
        id,
        cd?.mitarbeiterLeistung,
        isHJ2Freigegeben
      );

      return {
        id,
        name: String(ma?.name || `Mitarbeiter ${idx + 1}`),
        taetigkeit: String(ma?.taetigkeit || ''),
        scope,
        grundgehaltBrutto,
        lohnzusatz,
        bonus,
        wochenstunden,
        monatsstunden,
        arbeitstageProWoche,
        lohnnebenkostenSatzPercent: lnkSatz,
        agVollkostenMonat,
        stundenlohnGesamt,
        istUmsatzMonatBrutto: histBrutto > 0 ? histBrutto : sollUmsatzMonatBrutto,
        sollUmsatzMonatBrutto,
        ...(ma?.linkedProductiveEmployeeId
          ? { linkedProductiveEmployeeId: String(ma.linkedProductiveEmployeeId) }
          : {}),
      };
    });
  };

  const prodList = cloneEmployeeList(calc.personalData || [], 'produktiv');
  const rawUnprodList = cloneEmployeeList(calc.unproductivePersonalData || [], 'unproduktiv');
  // Falls bei 'Unprod. Anteil' noch keine linkedProductiveEmployeeId gesetzt war, per Name mit prodList verknüpfen
  const unprodList = rawUnprodList.map((u) => {
    if (u.taetigkeit === 'Unprod. Anteil' && !u.linkedProductiveEmployeeId) {
      const matched = prodList.find(
        (p) => p.name.trim().toLowerCase() === u.name.trim().toLowerCase()
      );
      if (matched) {
        return { ...u, linkedProductiveEmployeeId: matched.id };
      }
    }
    return u;
  });
  const azubiList = cloneEmployeeList(calc.azubiAssistenzData || [], 'azubi');
  const employees: PlanningBaselineEmployee[] = [...prodList, ...unprodList, ...azubiList];

  // --- Deep-Clone Materialliste ---
  const rawMaterialListe =
    cd?.kalkulator?.materialListe || st?.materialListe || [];
  const materialListe: PlanningMaterialItem[] = Array.isArray(rawMaterialListe)
    ? rawMaterialListe.map((m: any, idx: number) => ({
        id: String(m?.id ?? `mat_${idx}`),
        name: String(m?.name || 'Material'),
        gebinde: parseVal(m?.gebinde),
        ekPreis: parseVal(m?.ekPreis),
      }))
    : [];

  // --- Deep-Clone Geräteliste ---
  const zeitraumMonate = Math.max(
    1,
    parseVal(cd?.bwaData?.zeitraum ?? calc.zeitraum, 12)
  );
  const rawGeraeteListe = Array.isArray(calc.geraeteListe) ? calc.geraeteListe : [];
  const geraeteListe: PlanningDeviceItem[] = rawGeraeteListe
    .filter((g: any) => g && g.id)
    .map((g: any) => {
      const id = String(g.id);
      const periodVal = parseVal(cd?.bwaData?.[id]);
      const kostenMonatNetto = zeitraumMonate > 0 ? periodVal / zeitraumMonate : 0;
      return {
        id,
        name: String(g.name || 'Gerät'),
        kostenMonatNetto,
        kostenProzent: parseVal(g.kostenProzent),
      };
    });

  // --- Aktiver Kostendeckungsfaktor ---
  const kdf1 = Math.max(2.8, parseVal(cd?.lohnfaktorData?.kostendeckungsfaktor1, 2.8));
  const kdf2 = Math.max(2.8, parseVal(cd?.lohnfaktorData?.kostendeckungsfaktor2, kdf1));
  const kostendeckungsfaktorAktiv = hj1Locked || isHJ2Freigegeben ? kdf2 : kdf1;

  // --- Produktive Teamstunden ---
  const prodMonatsstundenMaOhneAzubis = prodList.reduce(
    (sum, e) => sum + e.monatsstunden,
    0
  );
  const inhaberMonatsstunden = parseVal(calc.monatsstundenChef);
  const inhaberProdUnternehmerlohn = parseVal(calc.prodUnternehmerlohn);
  const summeProduktiveMonatsstundenTeam =
    prodMonatsstundenMaOhneAzubis +
    (inhaberProdUnternehmerlohn > 0 ? inhaberMonatsstunden : 0);

  const inhaberStundenlohnSoll =
    parseVal(effectiveCd?.lohnfaktorData?.unternehmerStundenlohn_editable) > 0
      ? parseVal(effectiveCd?.lohnfaktorData?.unternehmerStundenlohn_editable)
      : inhaberMonatsstunden > 0
        ? inhaberProdUnternehmerlohn / inhaberMonatsstunden
        : 0;

  const baseline: PlanningBaselineData = {
    sourceMandantUid: meta?.mandantUid ?? null,
    sourceYear: meta?.year ?? null,
    capturedAt: new Date().toISOString(),
    zeitraumMonate,

    istMonatsumsatzNetto: parseVal(calc.monatsumsatzNetto),
    istMonatsumsatzBrutto: parseVal(calc.monatsumsatzBrutto),
    betriebskostenMonatNetto: parseVal(calc.betriebskostenMonat),
    positiveWerteMonatNetto: parseVal(calc.positiveWerteMonat),
    materialkostenMonatNetto: parseVal(calc.materialkostenMonat),
    baseMaterialQuotePercent: parseVal(calc.baseMaterialProzent),
    geraetekostenMonatNetto: parseVal(calc.geraetekostenMonat),
    gemeinkostenGesamtMonatNetto: parseVal(calc.gemeinkostenGesamtMonat),
    sollumsatzMonatNetto: parseVal(calc.sollumsatz),
    kalkSollumsatzInklFortzahlungNetto: parseVal(calc.kalkSollumsatzInklFortzahlung),

    employees,
    produktiveLohnkostenMaOhneAzubisMonat: parseVal(
      calc.produktiveLohnkostenMitarbeiterOhneAzubis
    ),
    unproduktiveLohnkostenMaMonat: parseVal(calc.unproduktiveLohnkostenMitarbeiter),
    azubiKostenGesamtMonat: parseVal(calc.azubiKostenGesamt),
    teamStundenlohn: parseVal(calc.teamStundenlohn),
    summeProduktiveMonatsstundenTeam,

    inhaberEntnahmeNetto: parseVal(cd?.unternehmerData?.entnahme),
    inhaberSozialkosten: parseVal(cd?.unternehmerData?.sozialkosten),
    inhaberEchterBedarfGesamt: parseVal(calc.echterBedarfGesamt ?? calc.gesamtlohn),
    inhaberEinkommensteuerSatzPercent: parseVal(calc.einkommenssteuerSatz),
    inhaberEinkommensteuerMonat: parseVal(calc.einkommenssteuerUnternehmer),
    inhaberProdWochenstunden: parseVal(calc.prodWochenstundenChef),
    inhaberMonatsstunden,
    inhaberStundenlohnSoll,
    inhaberProdUnternehmerlohn,
    inhaberUnprodUnternehmerlohn: parseVal(calc.unprodUnternehmerlohn),
    inhaberEigenumsatzBruttoMonat: parseVal(
      calc.kalkulierterUnternehmerUmsatzBrutto ?? calc.erwarteterBruttoUmsatz
    ),

    lohnfaktorNetto: parseVal(calc.lohnfaktorNetto),
    lohnfaktorBrutto: parseVal(calc.lohnfaktorBrutto),
    lohnfaktorFortzahlung: parseVal(calc.lohnfaktorFortzahlung),
    lohnfaktorAktiv,
    kostendeckungsfaktorAktiv,
    gemeinkostenfaktor: parseVal(calc.gemeinkostenfaktor),
    materialkostenfaktor: parseVal(calc.materialkostenfaktor),
    geraetekostenfaktor: parseVal(calc.geraetekostenfaktor),
    prodLohnkostenfaktor: parseVal(calc.prodLohnkostenfaktor),
    unprodUnternehmerlohnPercent: parseVal(calc.unprodUnternehmerlohnPercent),
    ueberschussMonatNetto: parseVal(calc.ueberschuss),
    ueberschussProzent: parseVal(calc.ueberschussProzent),
    inhaberErtragSollMonat: parseVal(calc.inhaberErtragSoll),
    rentabilitaetSollProzent: parseVal(calc.rentabilitaetSoll),

    materialListe,
    geraeteListe,
    barometerStundenfaktor: (() => {
      const useEchter = Boolean(cd?.kalkulator?.useEchterStundensatz);
      const echter = parseVal(cd?.kalkulator?.echterStundensatz);
      const baro = parseVal(cd?.kalkulator?.barometerStundenfaktor);
      return useEchter && echter > 0 ? echter : baro;
    })(),
    barometerStundenfaktorMitMaterial: (() => {
      const useEchterMat = Boolean(cd?.kalkulator?.useEchterStundensatzMat);
      const echterMat = parseVal(cd?.kalkulator?.echterStundensatzMat);
      if (useEchterMat && echterMat > 0) return echterMat;
      const list = Array.isArray(cd?.kalkulator?.priceParameterListe)
        ? cd.kalkulator.priceParameterListe
        : [];
      const found =
        list.find(
          (p: any) =>
            String(p?.desc || '').toLowerCase().includes('stundensatz') ||
            String(p?.desc || '').toLowerCase().includes('verrechnungssatz')
        ) ||
        list.reduce(
          (max: any, p: any) =>
            parseVal(p?.preis) > parseVal(max?.preis) ? p : max,
          list[0] || null
        );
      return parseVal(found?.preis);
    })(),
  };

  return { baseline, globalSettings };
}
